import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import { USER_PROFILE_ATTACHMENT_KEYS, STATUS_IDS, USER_ROLES_IDS, PERMIT_TYPES } from "../../../constants/common"
import useAuthStore from "../../../store/Auth"
import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import toast from "react-hot-toast"
import { useAddUpdateEntryStickerForParking } from "../../../hooks/permit/mutation"
import { fileToBase64 } from "../../../lib/functions"
import { APP_ROUTES } from "../../../routes"
import { useLocation, useNavigate } from "react-router-dom"
import { UseFormHandleSubmit } from "react-hook-form"

interface FormData {
    name: string;
    department: string;
    jobTitle: string;
    mobileNo: string;
    officePhoneNo: string;
    employeeNumber: string;
    presentedTo?: string;
    uploadNationalId?: any;
    uploadEmployeeCard?: any;
}

interface ActionButtonGroupProps {
    parkingSticker: any;
    isAcknowledgeStickerReceipt: boolean;
    isFieldsDisabled: boolean;
    isPermitApprovedByNextApprover: boolean;
    checkedBuildingIds: number[];
    vehicleAccessIds: number[];
    addedVehicles: any[];
    handleSubmit: UseFormHandleSubmit<{
        presentedTo?: string | undefined;
        name: string;
        department: string;
        jobTitle: string;
        mobileNo: string;
        officePhoneNo: string;
        employeeNumber: string;
    }, undefined>
    filesBase64: Record<string, string>
    fileTypes: Record<string, string>
}

const ActionButtonGroup = ({
    parkingSticker,
    isAcknowledgeStickerReceipt,
    isFieldsDisabled,
    handleSubmit,
    isPermitApprovedByNextApprover,
    checkedBuildingIds,
    vehicleAccessIds,
    addedVehicles,
    filesBase64,
    fileTypes,
}: ActionButtonGroupProps) => {
    const { user } = useAuthStore()

    const { t } = useTranslation()

    const navigate = useNavigate()

    const location = useLocation()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);
    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);
    const isApprovePage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_PARKING_STICKER_REQUEST,
        [location.pathname],
    );

    const {
        mutateAsync: AddUpdateEntryStickerForParking,
        isPending: isAddUpdateEntryStickerForParkingLoading,
    } = useAddUpdateEntryStickerForParking();

    const isApproveDisabled = useMemo(
        () =>
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (parkingSticker?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== parkingSticker?.n_PermitNextApproverRoleID),
        [
            parkingSticker?.n_ApprovalStatus,
            parkingSticker?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ]
    );

    const onApprove = useCallback(
        async () => {
            if (isApproveDisabled) return;

            setIsApprovePermitModalOpen(true);
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            parkingSticker?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [parkingSticker?.n_ApprovalStatus]
    );

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, parkingSticker?.permitEndDate, t]);

    const onSubmit = useCallback(async (data: FormData) => {
        if (vehicleAccessIds.length === 0) {
            toast.error(t("selectVehicleAccess"));
            return;
        } else if (checkedBuildingIds.length === 0) {
            toast.error(t("selectAreasToAccess"));
            return;
        } else if (addedVehicles.length === 0) {
            toast.error(t("addAVehicle"));
            return;
        }

        try {
            const body = {
                n_ParkingStickerRequestID: key ? Number(key) : 0,
                s_Name: data.name,
                s_Department: data.department,
                s_JobTitle: data.jobTitle,
                s_MobileNo: data.mobileNo,
                s_OfficePhone: data.officePhoneNo,
                s_EmployeeNumber: data.employeeNumber,
                vehicleAccess: vehicleAccessIds,
                s_HostEmployeeCardFileBase64: data.uploadEmployeeCard
                    ? await fileToBase64(data.uploadEmployeeCard)
                    : !key ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD] ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD]
                        : ''
                        : '',
                s_HostEmployeeCardContentType: data.uploadEmployeeCard
                    ? data.uploadEmployeeCard.type
                    : !key ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD]
                        ? fileTypes[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD]
                        : ''
                        : '',
                haveHostEmployeeCardFile: !!data.uploadEmployeeCard,

                s_HostNationalIDFileBase64: data.uploadNationalId
                    ? await fileToBase64(data.uploadNationalId)
                    : !key ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]
                        ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]
                        : ''
                        : '',
                s_HostNationalIDContentType: data.uploadNationalId
                    ? data.uploadNationalId.type
                    : !key ? filesBase64[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]
                        ? fileTypes[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]
                        : ''
                        : '',
                haveNationalIDCardFile: !!data.uploadNationalId,

                buildings: checkedBuildingIds,
                vehicles: await Promise.all(
                    addedVehicles.map(async (vehicle) => ({
                        n_ParkingStickerVehicleID: vehicle.id && typeof vehicle.id === 'number' ? vehicle.id : 0,
                        n_PlateSource: Number(vehicle.plateSource),
                        n_PlateLetter1: Number(vehicle.plateLetter1),
                        n_PlateLetter2: Number(vehicle.plateLetter2),
                        n_PlateLetter3: Number(vehicle.plateLetter3),
                        s_PlateNumber: vehicle.plateNumber,
                        s_ModelNumber: vehicle.model,
                        s_Color: vehicle.color,
                        s_StickerNumber: vehicle.stickerParkingNumber,
                        s_CopyNumber: vehicle.copyNumber,
                        s_VehicleRegistrationFileBase64: vehicle.uploadVehicleRegLicense &&
                            vehicle.uploadVehicleRegLicense instanceof File
                            ? await fileToBase64(vehicle.uploadVehicleRegLicense)
                            : '',
                        s_VehicleRegistrationContentType: vehicle.uploadVehicleRegLicense &&
                            vehicle.uploadVehicleRegLicense instanceof File
                            ? vehicle.uploadVehicleRegLicense.type
                            : '',
                        haveVehicleRegistrationFile: !!vehicle.uploadVehicleRegLicense,
                    }))),
            }

            await AddUpdateEntryStickerForParking(body);

            toast.success(
                key
                    ? t("entryStickerForParkingUpdatedSuccessfully")
                    : t("entryStickerForParkingAddedSuccessfully")
            );

            setTimeout(() => {
                navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
            }, 700);
        } catch (error) {
            toast.error(t("somethingWentWrong"));
        }
    }, [
        addedVehicles,
        checkedBuildingIds,
        vehicleAccessIds,
        key,
        navigate,
        t,
        AddUpdateEntryStickerForParking,
        filesBase64,
        fileTypes,
    ])

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={isAddUpdateEntryStickerForParkingLoading}
                                    disabled={
                                        !isAcknowledgeStickerReceipt ||
                                        isAddUpdateEntryStickerForParkingLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}

            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdateEntryStickerForParkingLoading}
                            disabled={
                                !isAcknowledgeStickerReceipt ||
                                isAddUpdateEntryStickerForParkingLoading
                            }
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.PARKING_STICKER_REQUEST}
                permitId={parkingSticker?.n_ParkingStickerRequestID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.PARKING_STICKER_REQUEST}
                permitId={parkingSticker?.n_ParkingStickerRequestID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default memo(ActionButtonGroup)
